/**
 * FileUpload utility functions for error handling, retry logic, and file validation
 */

import type { UploadError, XhrUploadOptions } from './types';

// Constants
export const DEFAULT_MAX_RETRIES = 3;
export const DEFAULT_RETRY_DELAY_MS = 1000;
export const DEFAULT_TIMEOUT_MS = 60000; // 60 seconds
export const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const DEFAULT_ACCEPTED_TYPES = '.pdf,.jpg,.png,.log';

/**
 * Classifies an error and determines if it's retryable
 */
export const classifyError = (error: unknown, statusCode?: number): UploadError => {
  // Network errors (no response received)
  if (error instanceof TypeError && error.message.includes('network')) {
    return {
      type: 'NETWORK_ERROR',
      message: 'Network connection failed. Please check your internet connection.',
      retryable: true,
    };
  }

  // Timeout errors
  if (error instanceof Error && error.message.toLowerCase().includes('timeout')) {
    return {
      type: 'TIMEOUT_ERROR',
      message: 'Upload timed out. Please try again.',
      retryable: true,
    };
  }

  // HTTP status code based classification
  if (statusCode) {
    // Rate limiting (429)
    if (statusCode === 429) {
      return {
        type: 'RATE_LIMIT_ERROR',
        message: 'Too many requests. Please wait a moment and try again.',
        retryable: true,
        statusCode,
      };
    }

    // Authentication/Authorization errors (401, 403)
    if (statusCode === 401 || statusCode === 403) {
      return {
        type: 'AUTH_ERROR',
        message: 'Upload authorization failed. Please refresh the page and try again.',
        retryable: false,
        statusCode,
      };
    }

    // Server errors (5xx) - retryable
    if (statusCode >= 500 && statusCode < 600) {
      return {
        type: 'SERVER_ERROR',
        message: 'Server error occurred. Retrying...',
        retryable: true,
        statusCode,
      };
    }

    // S3 specific errors (typically 4xx from S3)
    if (statusCode >= 400 && statusCode < 500) {
      return {
        type: 'S3_ERROR',
        message: `Upload failed (${statusCode}). The upload URL may have expired.`,
        retryable: statusCode === 408 || statusCode === 409, // Request timeout or conflict
        statusCode,
      };
    }
  }

  // Generic error handling
  const message = error instanceof Error ? error.message : 'Upload failed';
  return {
    type: 'UNKNOWN_ERROR',
    message,
    retryable: false,
  };
};

/**
 * Calculates exponential backoff delay with jitter
 */
export const getRetryDelay = (
  retryCount: number,
  baseDelay: number = DEFAULT_RETRY_DELAY_MS
): number => {
  // Exponential backoff: baseDelay * 2^retryCount
  const exponentialDelay = baseDelay * Math.pow(2, retryCount);
  // Add jitter (±25%) to prevent thundering herd
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
  // Cap at 30 seconds
  return Math.min(exponentialDelay + jitter, 30000);
};

/**
 * Creates a promise that rejects after a timeout
 */
export const createTimeoutPromise = (timeoutMs: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Request timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });
};

/**
 * Sleep utility for retry delays
 */
export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Formats file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Validates a file against size and type constraints
 */
export const validateFile = (
  file: File,
  maxFileSize: number,
  acceptedTypes: string
): string | null => {
  if (file.size > maxFileSize) {
    return `File size exceeds ${formatFileSize(maxFileSize)} limit`;
  }

  const acceptedTypesList = acceptedTypes.split(',').map((t) => t.trim().toLowerCase());
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

  if (!acceptedTypesList.includes(fileExtension)) {
    return `File type ${fileExtension} is not supported`;
  }

  return null;
};

/**
 * Gets the file icon name based on file extension
 */
export const getFileIconName = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'document';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'image';
    case 'log':
    case 'txt':
      return 'document';
    default:
      return 'document';
  }
};

/**
 * Creates an XHR upload with progress tracking
 */
export const createXhrUpload = (options: XhrUploadOptions): Promise<void> => {
  const { uploadUrl, file, timeoutMs, onProgress, abortSignal, tenantId } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Handle abort
    abortSignal.addEventListener('abort', () => {
      xhr.abort();
      reject(new Error('Upload aborted'));
    });

    // Progress tracking
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    // Success handler
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const error = new Error(`S3 upload failed with status ${xhr.status}`);
        (error as Error & { statusCode?: number }).statusCode = xhr.status;
        reject(error);
      }
    });

    // Error handler
    xhr.addEventListener('error', () => {
      reject(new TypeError('Network request failed'));
    });

    // Timeout handler
    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timed out'));
    });

    // Abort handler
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.timeout = timeoutMs;
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    if (tenantId) {
      xhr.setRequestHeader('x-amz-meta-x-tenant-id', tenantId);
    }
    xhr.send(file);
  });
};
